import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  notification,
  Select,
  Spin,
  Switch,
  Table,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import SharedService from "../../../services/shared/service";
import { useAuth } from "oidc-react";
import SEO from "../../../components/SEO";
import { formatDate } from "../../../utils/datetime.utils";

const MAILBOX_USER_PATH = "mailbox/user";
// POST (create), POST /search, PATCH (update) and DELETE /{id}.
const MAILBOX_USER_SEARCH_PATH = `${MAILBOX_USER_PATH}/search`;

const pageSEOSettings = {
  title: "Mailbox Users",
};

// MailboxDTO: { id, email, name, replyToEmail, isActive, isCancelled }
const mailboxName = (mailbox) =>
  typeof mailbox === "string" ? mailbox : (mailbox?.name ?? "");

const mailboxEmail = (mailbox) =>
  typeof mailbox === "string" ? "" : (mailbox?.email ?? "");

const mailboxLabel = (mailbox) => mailboxName(mailbox) || mailboxEmail(mailbox);

const yesNo = (value) => (value ? "Yes" : "No");

const MailboxUsers = () => {
  const { userData } = useAuth();
  const token = userData?.access_token;

  const [mailboxUsers, setMailboxUsers] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [filters, setFilters] = useState({
    userEmail: "",
    mailBoxEmail: "",
  });
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [sortedInfo, setSortedInfo] = useState({});
  const [listPagination, setlistPagination] = useState({
    totalItemCount: 0,
    pageSize: 10,
    pageNumber: 1,
  });

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const notifyError = (error, fallback) => {
    notification.error({
      message: "Error",
      description: error?.response?.statusText ?? fallback,
    });
  };

  useEffect(() => {
    fetchMailboxOptions();
  }, []);

  useEffect(() => {
    fetchMailboxUsers();
  }, [listPagination.pageNumber, sortedInfo, searchTrigger]);

  const fetchMailboxOptions = async () => {
    try {
      const response = await SharedService.get({
        token,
        url: "reference/mailboxes",
      });

      setMailboxes(Array.isArray(response) ? response : []);
    } catch (error) {
      notifyError(error, "Could not load mailboxes.");
    }
  };

  // /reference/mailboxes returns { id, name, value } — the numeric id, the
  // display name and the address. MailboxUserRequest wants the id,
  // SearchMailboxUserRequest wants the address.
  const mailboxFormOptions = mailboxes.map((item) => ({
    label: item.name ?? item.value,
    value: item.id,
    key: item.id,
  }));

  const mailboxFilterOptions = mailboxes.map((item) => ({
    label: item.name ?? item.value,
    value: item.value,
    key: item.id,
  }));

  const findMailboxOption = (mailbox) => {
    const id = typeof mailbox === "object" ? mailbox?.id : undefined;
    return id === undefined || id === null
      ? undefined
      : mailboxFormOptions.find(
          (option) => String(option.value) === String(id),
        );
  };

  const fetchMailboxUsers = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: listPagination.pageNumber,
        pageSize: listPagination.pageSize,
        orderBy:
          sortedInfo.columnKey && sortedInfo.order
            ? `${sortedInfo.columnKey}:${
                sortedInfo.order === "ascend" ? "Asc" : "Desc"
              }`
            : "id:Asc",
      };

      // SearchMailboxUserRequest — every field is optional, so only send the
      // ones that are actually set.
      const data = {
        ...(filters.userEmail !== "" && { userEmail: filters.userEmail }),
        ...(filters.mailBoxEmail !== "" && {
          mailBoxEmail: filters.mailBoxEmail,
        }),
      };

      const response = await SharedService.post({
        token,
        url: MAILBOX_USER_SEARCH_PATH,
        data,
        params,
      });

      setMailboxUsers(response?.items ?? []);
      setlistPagination((prev) => ({
        ...prev,
        totalItemCount: response?.totalItemCount ?? 0,
      }));
    } catch (error) {
      notifyError(error, "Could not load mailbox users.");
    } finally {
      setLoading(false);
    }
  };

  // MailboxUserRequest. UpdatedBy/UpdatedOn are internal on the server, so they
  // are deliberately not sent.
  const buildPayload = (values) => ({
    userEmail: values.userEmail,
    mailboxId: values.mailboxId,
  });

  const addMailboxUser = async (values) => {
    setLoading(true);
    try {
      await SharedService.post({
        token,
        url: MAILBOX_USER_PATH,
        data: buildPayload(values),
      });
      notification.success({ message: "Added Successfully" });
      fetchMailboxUsers();
    } catch (error) {
      notifyError(error, "Could not add the mailbox user.");
    } finally {
      setLoading(false);
    }
  };

  const editMailboxUser = async (values) => {
    setLoading(true);
    try {
      // UpdateMailboxUserRequest adds Id and IsActive.
      await SharedService.patch({
        token,
        url: MAILBOX_USER_PATH,
        data: {
          id: values.id,
          ...buildPayload(values),
          isActive: values.isActive,
        },
      });
      notification.success({ message: "Updated Successfully" });
      fetchMailboxUsers();
    } catch (error) {
      notifyError(error, "Could not update the mailbox user.");
    } finally {
      setLoading(false);
    }
  };

  const deleteMailboxUser = async (id) => {
    setLoading(true);
    try {
      await SharedService.delete({
        token,
        url: `${MAILBOX_USER_PATH}/${id}`,
      });
      notification.success({ message: "Deleted Successfully" });
      fetchMailboxUsers();
    } catch (error) {
      notifyError(error, "Could not delete the mailbox user.");
    } finally {
      setLoading(false);
    }
  };

  const runSearch = () => {
    setlistPagination((prev) => ({ ...prev, pageNumber: 1 }));
    setSearchTrigger((n) => n + 1);
  };

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value ?? "" }));

  const showAddModal = () => {
    addForm.resetFields();
    setAddModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      id: record.id,
      userEmail: record.userEmail,
      mailboxId: findMailboxOption(record.mailbox)?.value ?? record.mailbox?.id,
      isActive: record.isActive,
    });
    setEditModalVisible(true);
  };

  const showDeleteModal = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const handleCancel = () => {
    setAddModalVisible(false);
    setEditModalVisible(false);
    setDeleteModalVisible(false);
  };

  const handleAdd = () => {
    addForm.validateFields().then((values) => {
      addMailboxUser(values);
      addForm.resetFields();
      setAddModalVisible(false);
    });
  };

  const handleEdit = () => {
    editForm.validateFields().then((values) => {
      editMailboxUser(values);
      setEditModalVisible(false);
    });
  };

  const handleDelete = () => {
    if (selectedRecord) {
      deleteMailboxUser(selectedRecord.id);
    }
    setSelectedRecord(null);
    setDeleteModalVisible(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
    setlistPagination((prev) => ({ ...prev, pageNumber: pagination.current }));
  };

  const paginationProps = {
    showSizeChanger: false,
    total: listPagination.totalItemCount,
    pageSize: listPagination.pageSize,
    current: listPagination.pageNumber,
  };

  const dateColumn = (title, key) => ({
    title,
    dataIndex: key,
    key,
    width: 150,
    sorter: true,
    sortOrder: sortedInfo.columnKey === key && sortedInfo.order,
    render: (timestamp) =>
      timestamp ? (
        <Tooltip title={formatDate(timestamp, "dd-MMM-yyyy hh:mm tt")}>
          <div>
            <div>{formatDate(timestamp, "dd-MMM-yyyy")}</div>
            <div className="cell-sub">{formatDate(timestamp, "hh:mm tt")}</div>
          </div>
        </Tooltip>
      ) : (
        ""
      ),
  });

  const flagColumn = (title, key) => ({
    title: <div className="flex items-center justify-center">{title}</div>,
    dataIndex: key,
    key,
    width: 110,
    sorter: true,
    sortOrder: sortedInfo.columnKey === key && sortedInfo.order,
    render: (value) => (
      <Tooltip title={yesNo(value)}>
        <span className="flex items-center justify-center">{yesNo(value)}</span>
      </Tooltip>
    ),
  });

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      width: 90,
      sorter: true,
      sortOrder: sortedInfo.columnKey === "id" && sortedInfo.order,
      render: (id) => (
        <Tooltip title={id} placement="topLeft">
          <div>{id}</div>
        </Tooltip>
      ),
    },
    {
      title: "User Email",
      dataIndex: "userEmail",
      key: "userEmail",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "userEmail" && sortedInfo.order,
      render: (email) => (
        <Tooltip title={email} placement="topLeft">
          <div>{email}</div>
        </Tooltip>
      ),
    },
    {
      title: "Mailbox",
      dataIndex: "mailbox",
      key: "mailbox",
      render: (mailbox) => (
        <Tooltip title={mailboxEmail(mailbox)} placement="topLeft">
          <div>
            <div>{mailboxName(mailbox)}</div>
            <div className="cell-sub">{mailboxEmail(mailbox)}</div>
          </div>
        </Tooltip>
      ),
    },
    flagColumn("Active", "isActive"),
    flagColumn("Cancelled", "isCancelled"),
    dateColumn("Created On", "createdOn"),
    dateColumn("Updated On", "updatedOn"),
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "updatedBy" && sortedInfo.order,
      render: (value) => (
        <Tooltip title={value} placement="topLeft">
          <div>{value}</div>
        </Tooltip>
      ),
    },
    {
      title: <div className="flex justify-end">Actions</div>,
      key: "actions",
      width: 120,
      render: (text, record) => (
        <div className="flex justify-end gap-3">
          <Tooltip title="Edit">
            <EditOutlined
              style={{ fontSize: 20 }}
              onClick={() => showEditModal(record)}
              className="cursor-pointer"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined
              style={{ fontSize: 20 }}
              onClick={() => showDeleteModal(record)}
              className="cursor-pointer text-red-500"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const fieldLabel = (text) => (
    <span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">
      {text}
    </span>
  );

  const emailRules = [
    { required: true, message: "Please enter an email address." },
    { type: "email", message: "Please enter a valid email address." },
  ];

  const mailboxRules = [
    { required: true, message: "Please select a mailbox." },
  ];

  // A record's mailbox is not guaranteed to appear in the reference list, and
  // without a matching option antd renders the raw value instead of the name.
  const withCurrentMailbox = (record) => {
    const id = record?.mailbox?.id;
    if (id === undefined || id === null || findMailboxOption(record?.mailbox)) {
      return mailboxFormOptions;
    }
    return [
      { label: mailboxLabel(record.mailbox), value: id, key: id },
      ...mailboxFormOptions,
    ];
  };

  const mailboxSelect = (options) => (
    <Select
      placeholder="Select the mailbox"
      options={options}
      showSearch
      optionFilterProp="label"
      filterOption={(input, option) =>
        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
      }
    />
  );

  return (
    <div>
      <SEO {...pageSEOSettings} />
      <div className="px-4 sm:px-7 py-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[22px] font-semibold text-[#0B1220] leading-tight">
              Mailbox Users
            </div>
            <div className="text-[13px] text-[#7A849A] mt-1">
              Control which users have access to each queue mailbox.
            </div>
          </div>
          <Button type="primary" onClick={showAddModal}>
            Add mailbox user
          </Button>
        </div>
      </div>

      <div className="px-4 sm:px-7 pb-7 relative">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <Input
            placeholder="Search user email…"
            prefix={<SearchOutlined className="text-[#7A849A]" />}
            className="w-[280px]"
            value={filters.userEmail}
            allowClear
            onChange={(e) => updateFilter("userEmail", e.target.value)}
            onPressEnter={runSearch}
          />
          <Select
            allowClear
            placeholder="All mailboxes"
            value={filters.mailBoxEmail || undefined}
            className="w-[260px]"
            options={mailboxFilterOptions}
            onChange={(value) => updateFilter("mailBoxEmail", value)}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={runSearch}>
            Search
          </Button>
        </div>

        {loading && (
          <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-10 rounded-[14px]">
            <Spin size="large" className="custom-spin" />
          </div>
        )}
        <Table
          dataSource={mailboxUsers}
          rowKey="id"
          columns={columns}
          pagination={paginationProps}
          onChange={handleTableChange}
          scroll={{ x: 1100 }}
        />
      </div>

      {/* Add Modal */}
      <Modal
        title="Add mailbox user"
        open={addModalVisible}
        onOk={handleAdd}
        onCancel={handleCancel}
        okText="Add"
        cancelText="Cancel"
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="userEmail"
            label={fieldLabel("User Email")}
            rules={emailRules}
          >
            <Input placeholder="Enter the user email" />
          </Form.Item>
          <Form.Item
            name="mailboxId"
            label={fieldLabel("Mailbox")}
            rules={mailboxRules}
          >
            {mailboxSelect(mailboxFormOptions)}
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit mailbox user"
        open={editModalVisible}
        onOk={handleEdit}
        onCancel={handleCancel}
        okText="Save"
        cancelText="Cancel"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="id" label={fieldLabel("ID")}>
            <Input readOnly disabled={true} />
          </Form.Item>
          <Form.Item
            name="userEmail"
            label={fieldLabel("User Email")}
            rules={emailRules}
          >
            <Input placeholder="Enter the user email" />
          </Form.Item>
          <Form.Item
            name="mailboxId"
            label={fieldLabel("Mailbox")}
            rules={mailboxRules}
          >
            {mailboxSelect(withCurrentMailbox(editingRecord))}
          </Form.Item>
          <Form.Item
            name="isActive"
            label={fieldLabel("Active")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Delete mailbox user"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={handleCancel}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        centered
      >
        <div className="text-[14px] text-[#374152]">
          Are you sure you want to remove {selectedRecord?.userEmail} from{" "}
          {mailboxLabel(selectedRecord?.mailbox)}?
        </div>
      </Modal>
    </div>
  );
};

export default MailboxUsers;
